import React, { useState, useReducer, useContext } from "react";
import { API, graphqlOperation } from "aws-amplify";
import NumberFormat from "react-number-format";
import { makeStyles } from "@material-ui/core/styles";
import Drawer from "@material-ui/core/Drawer";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Backdrop from "@material-ui/core/Backdrop";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import CloseIcon from "@material-ui/icons/Close";
import { createProduct, updateProduct } from "../graphql/mutations";
import logger from "../logger";
import { AppContext } from "../views/App";
import FileUploader from "./FileUploader";

export const PRODUCT_MAX_IMAGE_COUNT = 5;

const drawerWidth = "100vw";

const useStyles = makeStyles(theme => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: "#fafafa"
  },
  drawerContainer: {
    overflow: "auto"
  },
  backButton: {
    marginRight: theme.spacing(2)
  },
  closeButton: {
    marginLeft: theme.spacing(2)
  },
  title: {
    flexGrow: 1
  },
  control: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  button: {
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5)
  }
}));

const steps = {
  datos: 0,
  medias: 1
};

const initialState = {
  name: "",
  description: "",
  price: "",
  category: null,
  medias: []
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case "name":
    case "description":
    case "price":
    case "category":
      return { ...state, [type]: payload };
    case "add_medias":
      return { ...state, medias: [...state.medias, payload] };
    case "edit_medias":
      return { ...state, medias: payload };
    case "remove_medias":
      return {
        ...state,
        medias: [...state.medias.filter(m => m !== payload)]
      };
    case "reset":
      return { ...initialState };
    default:
      return { ...state };
  }
};

const ProductoCreate = ({ open, onClose, onCreated }) => {
  const classes = useStyles();
  const { store } = useContext(AppContext);
  const [enviando, setEnviando] = useState(false);
  const [step, setStep] = useState(0);
  const [state, dispatch] = useReducer(reducer, initialState);

  if (!store.id) return null;

  const validarDatos = () => {
    return (
      state.name !== "" &&
      state.price !== "" &&
      (store.body.categories.length === 1 ? true : state.category !== null)
    );
  };

  const validarMedias = () => {
    return state.medias.length > 0;
  };

  const validar = () => {
    switch (step) {
      case steps.datos:
        return validarDatos();
      case steps.medias:
        return validarMedias();
      default:
        return null;
    }
  };

  const crear = async () => {
    setEnviando(true);
    let vars = null;

    try {
      const { name, description, category } = state;

      const storeID = store.id;
      const createdAt = new Date().toISOString();
      const price = Number(`${state.price}`.replace(/\$/g, "").replace(/\./g, ""));
      const status = 1;

      const body = {
        category: category ? category.value : 0,
        description
      };

      vars = {
        input: {
          storeID,
          createdAt,
          name,
          price,
          status,
          body: JSON.stringify(body),
          medias: []
        }
      };

      const result = await API.graphql(graphqlOperation(createProduct, vars));

      if (result && result.data) {
        const productId = result.data.createProduct.id;
        const apiMedias = [];

        for (let i = 0; i < state.medias.length; i++) {
          let media = state.medias[i];
          const mediaIndex = i + 1;

          media.bucket = process.env.REACT_APP_CF_BUCKET;
          media.name = productId + "-IDX" + mediaIndex + media.extension;
          media.storeId = store.id;
          media.productId = productId;
          media.key = media.storeId + "/" + media.productId + "/" + media.name;

          const initUrl = {
            body: {
              key: media.key,
              type: media.type
            }
          };

          const urlData = await API.post(process.env.REACT_APP_HUB_API, "/uploader", initUrl);

          let url = undefined;
          let uploaded = false;

          if (urlData.success && urlData.url) {
            url = urlData.url;
          }

          if (url) {
            const initPut = {
              method: "PUT",
              headers: {
                "x-amz-acl": "public-read"
              },
              body: media.blob
            };
            const upladedData = await fetch(urlData.url, initPut);

            if (upladedData.ok) {
              uploaded = true;
            }
          }

          if (uploaded) {
            const s3media = {
              key: media.key,
              name: media.name,
              extension: media.extension,
              size: media.size,
              type: media.type,
              bucket: process.env.REACT_APP_CF_BUCKET
            };

            apiMedias.push(s3media);
          }
        }

        vars = {
          input: {
            id: productId,
            medias: apiMedias
          }
        };

        await API.graphql(graphqlOperation(updateProduct, vars));

        const init = {
          body: {
            product: productId,
            store: storeID,
            name,
            data: JSON.stringify(updateProduct),
            status
          }
        };

        await API.post(process.env.REACT_APP_HUB_API, "/analytics/products", init);

        onCreated();
        dispatch({
          type: "reset"
        });
        setStep(0);
        onClose();
      }
    } catch (err) {
      logger("ProductoCreate.crear.error", { err, vars });
    }
    setEnviando(false);
  };

  const atras = () => {
    if (step === 0) {
      onClose();
    } else {
      setStep(s => --s);
    }
  };

  const siguiente = () => {
    if (step < steps.medias) {
      setStep(s => ++s);
    } else {
      crear();
    }
  };

  const close = () => {
    dispatch({
      type: "reset"
    });
    setStep(0);
    onClose();
  };

  const mediaEdit = (currentMedia, newMedia) => {
    const medias = state.medias.map(m => (m === currentMedia ? newMedia : m));
    dispatch({
      type: "edit_medias",
      payload: medias
    });
  };

  const renderDatosStep = () => {
    return (
      <>
        <TextField
          fullWidth
          label="Nombre *"
          className={classes.control}
          placeholder="Ej: Polerón azul"
          disabled={enviando}
          size="small"
          variant="standard"
          value={state.name}
          onChange={({ target }) => dispatch({ type: "name", payload: target.value })}
          InputLabelProps={{
            shrink: true
          }}
        />

        <TextField
          fullWidth
          multiline
          rows={6}
          className={classes.control}
          label="Descripción"
          placeholder="Ej: Polerón de buzo ondero"
          disabled={enviando}
          size="small"
          variant="standard"
          value={state.description}
          onChange={({ target }) => dispatch({ type: "description", payload: target.value })}
          InputLabelProps={{
            shrink: true
          }}
        />

        <NumberFormat
          fullWidth
          className={classes.control}
          value={state.price}
          thousandSeparator="."
          decimalSeparator=","
          prefix="$"
          customInput={TextField}
          label="Precio *"
          placeholder="Ej: $25.000"
          disabled={enviando}
          size="small"
          variant="standard"
          onValueChange={({ value }) => {
            dispatch({
              type: "price",
              payload: value
            });
          }}
          inputProps={{
            maxLength: 8
          }}
          InputLabelProps={{
            shrink: true
          }}
        />

        {store.body.categories.length > 1 && (
          <Autocomplete
            className={classes.control}
            options={store.body.categories}
            getOptionLabel={c => c.label}
            value={state.category}
            onChange={(_, value) => dispatch({ type: "category", payload: value })}
            renderInput={params => (
              <TextField
                {...params}
                fullWidth
                variant="standard"
                size="small"
                label="Categoría *"
                disabled={enviando}
              />
            )}
          />
        )}
      </>
    );
  };

  const renderMediasStep = () => {
    return (
      <FileUploader
        parent="product-create"
        data={state.medias}
        maxFiles={PRODUCT_MAX_IMAGE_COUNT}
        onAdd={newFile =>
          dispatch({
            type: "add_medias",
            payload: newFile
          })
        }
        onEdit={mediaEdit}
        onRemove={file =>
          dispatch({
            type: "remove_medias",
            payload: file
          })
        }
      />
    );
  };

  const renderCurrentStep = () => {
    switch (step) {
      case steps.datos:
        return renderDatosStep();
      case steps.medias:
        return renderMediasStep();
      default:
        return null;
    }
  };

  return (
    <Drawer
      className={classes.drawer}
      open={open}
      anchor="right"
      classes={{
        paper: classes.drawerPaper
      }}
    >
      <Backdrop className="backdrop" open={enviando}>
        <CircularProgress />
      </Backdrop>

      <AppBar position="sticky">
        <Toolbar>
          <IconButton
            className={classes.backButton}
            edge="start"
            color="inherit"
            onClick={() => (step === 0 ? close() : atras())}
          >
            <ArrowBackIcon />
          </IconButton>

          <Typography className={classes.title} variant="h6">
            Crear Producto
          </Typography>

          <IconButton className={classes.closeButton} edge="end" color="inherit" onClick={close}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm">
        {renderCurrentStep()}

        {step <= steps.medias && (
          <Button
            fullWidth
            className={classes.button}
            variant="contained"
            color="secondary"
            disabled={enviando || !validar()}
            onClick={siguiente}
          >
            {step < steps.medias ? "Siguiente" : "Guardar"}
          </Button>
        )}
      </Container>
    </Drawer>
  );
};

export default ProductoCreate;
